<template>
  <main class="report-payroll">
    <button-back />
    <h1 class="report-payroll__header">Отчет по расчетам с сотрудником</h1>
    <form
      class="report-payroll__filters"
      v-if="hasFetchedOptions"
      v-on:submit.prevent
    >
      <date-range-with-presets
        v-bind:presets="presetsOfDates"
        v-bind:value="selectedDates"
        custom-color-date="black"
        v-on:input="onInputDates" />
      <multiple-autocomplete-input
        v-if="isShowEmployeesSelectInput"
        label="Выберите сотрудников"
        removal-per-chip
        v-bind:items="employeeOptions"
        v-bind:value="selectedEmployees"
        v-on:input="onInputEmployees" />
    </form>
    <div
      class="report-payroll__scroll"
      v-if="isNotEmptyReport"
    >
      <table
        class="report-payroll-table report-payroll__table"
      >
        <thead class="report-payroll-table__head-line">
          <tr>
            <th
              class="report-payroll-table__head-cell"
              v-bind:key="title"
              v-for="title in columns"
            >{{ title }}</th>
          </tr>
        </thead>
        <tbody>
          <tr class="report-payroll-table__body-line">
            <td class="report-payroll-table__body-cell">
              <span><b>Итого</b></span>
            </td>
            <td
              v-for="index in 4"
              :key="index"
              class="report-payroll-table__body-cell"
            />
            <td class="report-payroll-table__body-cell">
              <span><b>{{ this.summary }}</b></span>
            </td>
          </tr>
          <tr
            class="report-payroll-table__body-line"
            v-bind:key="`table-rows:${rowIndex}`"
            v-for="({ cells, calculation_id, recipient_id }, rowIndex) in rows"
          >
            <td
              class="report-payroll-table__body-cell"
              v-bind:key="`table-rows:${rowIndex}:cells:${cellIndex}`"
              v-for="(title, cellIndex) in cells"
            >
              <router-link
                class="report-payroll-table__body-cell-link"
                title="Открыть детализацию"
                v-bind:to="{
                  name: 'ClinicPanelEmployeePayrollDetails',
                  params: {
                    calculationId: calculation_id,
                    employeeId: recipient_id,
                  },
                }"
              >
                <span>{{ title }}</span>
              </router-link>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <strong
      v-else-if="isFinishedReport"
      class="report-payroll__description"
    >
      <template v-if="canAccruePayroll">
        Начисления отсутствуют, создайте начисления в
        <router-link v-bind:to="{ name: 'ClinicPanelEmployeePayroll' }">
          Расчете ЗП
        </router-link>
      </template>
      <template v-else>Начисления отсутствуют</template>
    </strong>
  </main>
</template>
<script>
import ButtonBack from '@/components/ButtonBack.vue';
import DateRangeWithPresets from '@/components/DateRangeWithPresets.vue';
import MultipleAutocompleteInput from '@/components/MultipleAutocompleteInput.vue';
import { getISODateStringByDate } from '@/mixins/dateMixin';

/**
 * @description Идентификатор действия начисления заработной платы.
 */
const ACTION_ACCRUE_PAYROLL = 'accrue-payroll';

/**
 * @description Отчет по расчетам с сотрудником.
 * @type {Vue}
 */
export default {
  components: {
    ButtonBack,
    DateRangeWithPresets,
    MultipleAutocompleteInput,
  },
  computed: {
    /**
     * @description Может начислять заработную плату.
     * @type {boolean}
     */
    canAccruePayroll() {
      return this.isFinishedReport
        && this.actions.includes(ACTION_ACCRUE_PAYROLL);
    },
    /**
     * @description Не пустой отчет.
     * @type {boolean}
     */
    isNotEmptyReport() {
      return this.rows.length !== 0;
    },
    /**
     * @description Показывать поле выбора сотрудников.
     * @type {boolean}
     */
    isShowEmployeesSelectInput() {
      return this.employeeOptions.length !== 0;
    },
    /**
     * @description Набор предварительных дат для фильтрации.
     * @type {object[]}
     */
    presetsOfDates() {
      return this
        .periodOptions
        .map(({ from_to: [from, to], title, value }) => {
          const periodDates = [new Date(from), new Date(to)];
          return {
            id: value,
            label: title,
            title,
            toValue: () => periodDates,
          };
        });
    },
  },
  created() {
    this
      .fetchOptions()
      .then(() => {
        this.selectedDates = this.todayDates;
        this.fetchReport();
      })
      .finally(() => { this.$nextTick(() => { this.hasFetchedOptions = true; }); });
  },
  data() {
    return {
      actions: [],
      columns: [],
      employeeOptions: [],
      hasFetchedOptions: false,
      isFinishedReport: false,
      periodOptions: [],
      rows: [],
      selectedDates: [],
      selectedEmployees: [],
      todayDates: [],
      summary: 0,
    };
  },
  methods: {
    /**
     * @description Получить опции отчета.
     * @returns {Promise}
     */
    fetchOptions() {
      return this
        .$payrollGateway
        .viewReportOptions()
        .then((result) => {
          result.forEach(({ employees, periods, today_period: todayPeriod }) => {
            this.employeeOptions = employees;
            this.periodOptions = periods;
            this.todayDates = todayPeriod.map((value) => new Date(value));
          });
        });
    },
    /**
     * @description Получить данные отчета.
     * @returns {void}
     */
    fetchReport() {
      this.isFinishedReport = false;
      const [dateFrom, dateTo] = this.selectedDates.map(getISODateStringByDate);
      this
        .$payrollGateway
        .viewReport(dateFrom, dateTo, this.selectedEmployees)
        .then((result) => {
          result.forEach(({
            actions, columns, rows, summary,
          }) => {
            this.actions = actions;
            this.columns = columns;
            this.rows = rows;
            this.summary = summary;
          });
        })
        .finally(() => { this.isFinishedReport = true; });
    },
    /**
     * @description Обработать выбор периода для отчета.
     * @returns {void}
     */
    onInputDates(value) {
      if (value.length === 2) {
        this.selectedDates = value.sort(
          (first, second) => Number(first.getTime() > second.getTime()),
        );
        this.fetchReport();
      } else {
        this.selectedDates = value;
      }
    },
    /**
     * @description Обработать ввод получателей.
     * @returns {void}
     */
    onInputEmployees(value) {
      this.selectedEmployees = value;
      this.fetchReport();
    },
  },
  name: 'ClinicPanelReportEmployeesPayroll',
};
</script>
<style scoped lang="scss">
.report-payroll {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  overflow: scroll;
  padding: 2rem 1rem;
  width: 100%;
  &__filters {
    align-items: baseline;
    display: flex;
    flex-flow: row wrap;
    gap: 1rem;
  }
  &__description {
    a:hover {
      text-decoration: none;
    }
  }
  &__table {
    width: 100%;
  }
  &-table {
    border-collapse: collapse;
    font-size: .875rem;
    line-height: 1rem;
    overflow: auto;
    table-layout: fixed;
    &__body-cell {
      padding: 15px 8px;
      text-align: left;
      text-transform: none;
      white-space: nowrap;
    }
    &__body-cell-link {
      display: block;
      text-decoration: none;
      color: black !important;
      transition: color var(--animate-duration);
      cursor: pointer;
    }
    &__body-line {
      border-bottom: 1px solid var(--fill-color);
      &:not(:first-child):hover {
        background-color: #eaeff3;
        cursor: pointer;
      }
    }
    &__head-cell {
      padding: 10px 8px;
      text-align: left;
    }
    &__head-line {
      border-bottom: 2px solid var(--action-color);
      font-weight: 700;
    }
  }
}
@media screen and (max-width: 1570px) {
  .report-payroll {
    &__scroll {
      overflow: scroll;
    }
    &-table {
      width: 1450px;
    }
  }
}
</style>
